import { useEffect, useMemo, useState } from "react";
import { AgeBand, Sex, clampHeight, cmToFeetInches } from "@/utils/units";
import { CdfJson, interpPercentile, makeDemoCdf } from "@/utils/cdf";
import BellCurveSvg from "@/components/charts/BellCurveSvg";
import { Helmet } from "react-helmet-async";
import { Label } from "@/components/ui/label";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import AdSlot from "@/components/ads/AdSlot";
import CountryBadge from "@/components/common/CountryBadge";

const defaultCdfs: Record<Sex, CdfJson> = {
  male: makeDemoCdf(175, 7.5),
  female: makeDemoCdf(162, 6.5),
  combined: makeDemoCdf(169, 7.5),
};

function useQueryState() {
  const params = new URLSearchParams(location.search);
  const initialUnits = (params.get("u") as "cm" | "ftin") ||
    (localStorage.getItem("units") as "cm" | "ftin") ||
    "ftin";
  const initialSex = (params.get("sex") as Sex) ||
    ((localStorage.getItem("sex") as Sex) || "male");
  const initialBand = (params.get("band") as AgeBand) || "all";
  const initialCm = Number(params.get("h")) || 175;
  const [units, setUnits] = useState<"cm" | "ftin">(initialUnits);
  const [sex, setSex] = useState<Sex>(initialSex);
  const [band, setBand] = useState<AgeBand>(initialBand);
  const [cm, setCm] = useState<number>(initialCm);

  useEffect(() => {
    const p = new URLSearchParams();
    p.set("h", cm.toString());
    p.set("u", units);
    p.set("sex", sex);
    p.set("band", band);
    const newUrl = `${location.pathname}?${p.toString()}`;
    window.history.replaceState(null, "", newUrl);
    localStorage.setItem("units", units);
    localStorage.setItem("sex", sex);
  }, [cm, units, sex, band]);

  return { units, setUnits, sex, setSex, band, setBand, cm, setCm };
}

const Index = () => {
  const { units, setUnits, sex, setSex, band, setBand, cm, setCm } = useQueryState();

  const cdf = defaultCdfs[sex];
  const percentile = interpPercentile(cdf, clampHeight(cm));
  const mean = cdf.meta.mean_cm;
  const median = cdf.meta.median_cm;
  const delta = Number((cm - mean).toFixed(1));

  

  // Format height and deltas in selected units
  const formatImperial = (cmVal: number) => {
    const { ft, inch } = cmToFeetInches(cmVal);
    let inchRounded = Math.round(inch);
    let ftDisplay = ft;
    if (inchRounded === 12) { ftDisplay += 1; inchRounded = 0; }
    return `${ftDisplay} ft ${inchRounded} in`;
  };
  const formatHeight = (cmVal: number) => (units === "cm" ? `${cmVal.toFixed(1)} cm` : formatImperial(cmVal));
  const deltaLabel = useMemo(() => {
    if (units === "cm") return `${delta >= 0 ? "+" : ""}${delta.toFixed(1)} cm`;
    const inches = delta / 2.54;
    return `${inches >= 0 ? "+" : ""}${inches.toFixed(1)} in`;
  }, [units, delta]);

  const HEIGHT_MIN = 135;
  const HEIGHT_MAX = 210;

  const heightOptions = useMemo(() => {
    if (units === "cm") {
      return Array.from({ length: HEIGHT_MAX - HEIGHT_MIN + 1 }, (_, i) => ({
        value: HEIGHT_MIN + i,
        label: `${HEIGHT_MIN + i} cm`,
      }));
    }
    const seen = new Set<string>();
    const opts: { value: number; label: string }[] = [];
    for (let h = HEIGHT_MIN; h <= HEIGHT_MAX; h++) {
      const { ft, inch } = cmToFeetInches(h);
      let inchRounded = Math.round(inch);
      let ftDisplay = ft;
      if (inchRounded === 12) {
        ftDisplay += 1;
        inchRounded = 0;
      }
      const label = `${ftDisplay} ft ${inchRounded} in`;
      if (!seen.has(label)) {
        seen.add(label);
        opts.push({ value: h, label });
      }
    }
    return opts;
  }, [units]);

  const selectedHeightValue = useMemo(() => {
    const clamped = clampHeight(cm);
    if (units === "cm") return String(Math.round(clamped));
    const { ft, inch } = cmToFeetInches(clamped);
    let inchRounded = Math.round(inch);
    let ftDisplay = ft;
    if (inchRounded === 12) { ftDisplay += 1; inchRounded = 0; }
    const lbl = `${ftDisplay} ft ${inchRounded} in`;
    const found = (heightOptions as { value: number; label: string }[]).find(o => o.label === lbl);
    return found ? String(found.value) : String(Math.round(clamped));
  }, [cm, units, heightOptions]);

  // FAQ Schema
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How is the percentile calculated?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We use weighted empirical CDFs derived from CDC/NHANES adult height data. For demo, we approximate with a normal distribution.",
        },
      },
      {
        "@type": "Question",
        name: "Are results medical advice?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. This tool is informational and not medical advice.",
        },
      },
    ],
  };

   const BASELINE_TOTAL = 258000000;
   const MALE_SHARE = 0.49;
   const FEMALE_SHARE = 0.51;
   const TOTAL_MALE = Math.round(BASELINE_TOTAL * MALE_SHARE);
   const TOTAL_FEMALE = BASELINE_TOTAL - TOTAL_MALE;

   const pMale = interpPercentile(defaultCdfs.male, clampHeight(cm));
   const pFemale = interpPercentile(defaultCdfs.female, clampHeight(cm));

   const shorterMale = Math.round((pMale / 100) * TOTAL_MALE);
   const shorterFemale = Math.round((pFemale / 100) * TOTAL_FEMALE);
   const shorterTotal = shorterMale + shorterFemale;

   const tallerMale = TOTAL_MALE - shorterMale;
   const tallerFemale = TOTAL_FEMALE - shorterFemale;
   const tallerTotal = tallerMale + tallerFemale;

   return (
    <>
      <Helmet>
        <title>U.S. Adult Height Percentile Calculator</title>
        <meta name="description" content="Enter your height and sex to see your percentile vs U.S. adults. Includes mean, median, and a bell-curve visualization." />
        <link rel="canonical" href={window.location.origin + "/"} />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="container mx-auto py-8">
        {/* Top banner ad */}
        <AdSlot slot="banner_top" className="mb-6" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>U.S. Adult Height Percentile Calculator</CardTitle>
                <CountryBadge code="us" label="US" />
              </CardHeader>
              <CardContent className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="units">Units</Label>
                  <Select value={units} onValueChange={(v: any) => setUnits(v)}>
                    <SelectTrigger id="units">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cm">Centimeters</SelectItem>
                      <SelectItem value="ftin">Feet/Inches</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="height">Height</Label>
                  <Select
                    value={selectedHeightValue}
                    onValueChange={(v) => setCm(Number(v))}
                  >
                    <SelectTrigger id="height">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-50 bg-popover">
                      {(heightOptions as { value: number; label: string }[]).map((opt) => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sex">Sex</Label>
                  <Select value={sex} onValueChange={(v: any) => setSex(v)}>
                    <SelectTrigger id="sex">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="combined">Use Combined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Your results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Percentile</div>
                    <div className="text-2xl font-semibold">{percentile.toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Mean</div>
                    <div className="text-2xl font-semibold">{formatHeight(mean)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Median</div>
                    <div className="text-2xl font-semibold">{formatHeight(median)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Delta vs mean</div>
                    <div className="text-2xl font-semibold">{deltaLabel}</div>
                  </div>
                </div>
                <BellCurveSvg mean={mean} std={cdf.meta.std_cm || 7} value={cm} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Estimated shorter</div>
                    <div className="text-xl font-semibold">{shorterTotal.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">Males: {shorterMale.toLocaleString()} • Females: {shorterFemale.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Estimated taller</div>
                    <div className="text-xl font-semibold">{tallerTotal.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">Males: {tallerMale.toLocaleString()} • Females: {tallerFemale.toLocaleString()}</div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Note: Inputs are capped to 135–210 cm or 6 ft 11 in to reflect typical adult ranges. Values beyond this are extremely rare.
                </p>
               </CardContent>
            </Card>

            {/* In-content ad */}
            <AdSlot slot="incontent_1" />
          </div>

          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-20">
              <AdSlot slot="sidebar_1" />
            </div>
          </aside>
        </div>
      </div>
    </>
  );
};

export default Index;
